const MIN_FENCE_LENGTH = 3;
const MAX_FENCE_INDENT = 3;
const TAB_INDENT_WIDTH = 4;

interface Fence {
  marker: string;
  length: number;
  info: string;
}

const parseFence = (line: string): Fence | undefined => {
  let index = 0;
  let indent = 0;

  // A tab counts as four columns, so a tab-indented line can never be a fence.
  while (index < line.length && (line[index] === ' ' || line[index] === '\t')) {
    indent += line[index] === '\t' ? TAB_INDENT_WIDTH : 1;
    index++;
  }

  if (indent > MAX_FENCE_INDENT) {
    return undefined;
  }

  const marker = line[index];

  if (marker !== '`' && marker !== '~') {
    return undefined;
  }

  const start = index;

  while (index < line.length && line[index] === marker) {
    index++;
  }

  const length = index - start;

  if (length < MIN_FENCE_LENGTH) {
    return undefined;
  }

  return { marker, length, info: line.slice(index) };
};

/**
 * Determines whether the markdown ends inside a fenced code block whose closing fence has
 * not been received yet. While a response is streaming, an unterminated fence is still
 * parsed as a complete code block, so this is the only way to tell a finished block apart
 * from a block that is still being written.
 * @internal
 */
export const hasUnterminatedFence = (markdown: string): boolean => {
  let openFence: Fence | undefined;

  for (const line of markdown.split('\n')) {
    const fence = parseFence(line);

    if (!fence) {
      continue;
    }

    if (openFence === undefined) {
      // A backtick fence cannot have a backtick in its info string.
      if (fence.marker === '`' && fence.info.includes('`')) {
        continue;
      }
      openFence = fence;
      continue;
    }

    const isClosingFence =
      fence.marker === openFence.marker &&
      fence.length >= openFence.length &&
      fence.info.trim().length === 0;

    if (isClosingFence) {
      openFence = undefined;
    }
  }

  return openFence !== undefined;
};
